import classNames from "classnames";
import Tag from "components/Tag";
import { ReactNode } from "react";

type PdfAccessTagProps = {
  /** Icon to display - can be a className string, image URL, or React element */
  icon: string | ReactNode;
  /** Text to display next to the icon */
  text: string;
  /** Optional rotation in degrees */
  rotation?: number;
  /** Optional custom class name */
  className?: string;
  /** Alt text for image icons */
  alt?: string;
};

/**
 * @component PdfAccessTag
 * @description A reusable PDF access tag component that can display an icon and text
 * with optional rotation.
 */
export const PdfAccessTag = ({
  icon,
  text,
  rotation = 0,
  className,
  alt = "PDF Access",
}: PdfAccessTagProps) => {
  const isReactElement = typeof icon !== "string";
  const renderIcon = () => {
    if (typeof icon === "string") {
      // Check if it's an image URL
      if (icon.includes("/") || icon.includes(".")) {
        return (
          <div className="flex items-center justify-center overflow-hidden bg-bg-base w-4 h-4 rounded-sm">
            <img src={icon} alt={alt} className="w-4 h-4 object-contain" />
          </div>
        );
      } else {
        // Handle icon as a class inside Tag
        return icon;
      }
    }
    // React element
    return icon;
  };

  return (
    <Tag
      label={text}
      icon={renderIcon()}
      size="sm"
      className={classNames(
        isReactElement && "pl-0.5 gap-2",
        rotation && `transform-[rotate(${rotation}deg)]`,
        className
      )}
    />
  );
};
